import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { api, ApiError } from '../api.ts'
import { CATEGORIES } from '../format.ts'

interface Mf {
  id: string
  name: string
  orderCurrency: string
  defaultDealerDiscountPct?: string
}

interface NewOption {
  name: string
  price: string
  purchasePrice: string
  optionGroup: string
  /** pre-ticked on a new offer; in a group this marks the base variant */
  defaultSelected: boolean
}
const emptyOption = (): NewOption => ({ name: '', price: '', purchasePrice: '', optionGroup: '', defaultSelected: false })

/**
 * Add ONE machine by hand — the product that arrives without a price list.
 * The manufacturer is typed, not picked: a known name matches, a new one is
 * created with the machine. Equipment goes in right here, so the product is
 * ready to quote the moment it's saved; the spec can be filled on the card.
 */
export function NewMachinePage() {
  const nav = useNavigate()
  const [mfs, setMfs] = useState<Mf[]>([])
  const [manufacturerName, setManufacturerName] = useState('')
  const [model, setModel] = useState('')
  const [catalogCode, setCatalogCode] = useState('')
  const [category, setCategory] = useState('inne')
  const [offerTitle, setOfferTitle] = useState('')
  const [description, setDescription] = useState('')
  const [currencyNew, setCurrencyNew] = useState<'PLN' | 'EUR'>('PLN')
  const [retailPrice, setRetailPrice] = useState('')
  const [vatRate, setVatRate] = useState('23')
  const [listPrice, setListPrice] = useState('')
  const [dealerDiscountPct, setDealerDiscountPct] = useState('')
  const [options, setOptions] = useState<NewOption[]>([emptyOption()])
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    api.get<{ manufacturers: Mf[] }>('/api/manufacturers').then((r) => setMfs(r.manufacturers)).catch(() => {})
  }, [])

  // Matched by name, ignoring case, accents and stray spaces — the API's
  // pl_norm does the same, so the "new manufacturer" hint agrees with it.
  const norm = (s: string) =>
    s
      .trim()
      .toLocaleLowerCase('pl')
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/ł/g, 'l')
      .replace(/\s+/g, ' ')
  const mf = mfs.find((m) => norm(m.name) === norm(manufacturerName))
  const isNew = manufacturerName.trim().length > 0 && !mf
  const currency = mf?.orderCurrency ?? currencyNew

  // A known manufacturer brings its default dealer discount along.
  useEffect(() => {
    if (mf?.defaultDealerDiscountPct && !dealerDiscountPct) setDealerDiscountPct(String(Number(mf.defaultDealerDiscountPct)))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mf?.id])

  const num = (s: string) => (s.trim() === '' ? null : Number(s))
  const wypelnione = options.filter((o) => o.name.trim())

  function setOpt(i: number, patch: Partial<NewOption>) {
    setOptions((list) => {
      const next = list.map((o, j) => (j === i ? { ...o, ...patch } : o))
      // Always one blank row at the end, so the next item has somewhere to go.
      if (next[next.length - 1]?.name.trim()) next.push(emptyOption())
      return next
    })
  }

  async function submit() {
    setError(null)
    setBusy(true)
    try {
      const r = await api.post<{ id: string }>('/api/machines', {
        ...(mf ? { manufacturerId: mf.id } : { manufacturerName: manufacturerName.trim() }),
        model,
        catalogCode,
        category,
        offerTitle: offerTitle.trim() || null,
        description: description || null,
        retailPrice: num(retailPrice),
        retailCurrency: currency,
        vatRate: Number(vatRate),
        ...(listPrice ? { listPrice: Number(listPrice), listCurrency: currency, dealerDiscountPct: num(dealerDiscountPct) ?? 0 } : {}),
        options: wypelnione.map((o) => ({
          name: o.name.trim(),
          price: num(o.price) ?? 0,
          currency,
          ...(num(o.purchasePrice) != null ? { purchasePrice: num(o.purchasePrice) } : {}),
          optionGroup: o.optionGroup.trim() || null,
          defaultSelected: o.defaultSelected,
        })),
      })
      nav(`/katalog/${r.id}`)
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Błąd')
    } finally {
      setBusy(false)
    }
  }

  const inp = 'w-full px-3 py-1.5 border border-slate-300 rounded-md text-sm'
  const lbl = 'block text-xs text-slate-500 mb-0.5'
  const canSave = !busy && manufacturerName.trim() && model.trim() && catalogCode.trim()

  return (
    <div className="p-6 max-w-3xl">
      <Link to="/katalog" className="text-sm text-slate-500 hover:text-slate-700">← Katalog</Link>
      <h1 className="text-xl font-bold text-slate-800 mt-2 mb-1">Dodaj produkt</h1>
      <p className="text-sm text-slate-500 mb-5">Pojedyncza maszyna bez wgrywania cennika. Specyfikację techniczną uzupełnisz na karcie zaraz po zapisaniu.</p>

      {error && <div role="alert" className="mb-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">{error}</div>}

      <div className="bg-white border border-slate-200 rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <label className="col-span-2">
            <span className={lbl}>Producent *</span>
            <input
              value={manufacturerName}
              onChange={(e) => setManufacturerName(e.target.value)}
              list="producenci"
              placeholder="wpisz nazwę — znany producent podpowie się, nowy zostanie utworzony"
              className={inp}
              autoFocus
            />
            <datalist id="producenci">
              {mfs.map((m) => <option key={m.id} value={m.name} />)}
            </datalist>
            {mf && <span className="text-xs text-slate-500">Istniejący producent · ceny w {mf.orderCurrency}</span>}
            {isNew && (
              <span className="text-xs text-amber-700 flex items-center gap-2">
                Nowy producent „{manufacturerName.trim()}” — zostanie założony przy zapisie. Waluta:
                <select value={currencyNew} onChange={(e) => setCurrencyNew(e.target.value as 'PLN' | 'EUR')} className="border border-slate-300 rounded px-1 py-0.5 text-xs bg-white">
                  <option value="PLN">PLN</option>
                  <option value="EUR">EUR</option>
                </select>
              </span>
            )}
          </label>
          <label>
            <span className={lbl}>Model *</span>
            <input value={model} onChange={(e) => setModel(e.target.value)} placeholder="np. Opryskiwacz Power 1000" className={inp} />
          </label>
          <label>
            <span className={lbl}>Kod katalogowy *</span>
            <input value={catalogCode} onChange={(e) => setCatalogCode(e.target.value)} placeholder="symbol producenta" className={`${inp} font-mono`} />
          </label>
          <label>
            <span className={lbl}>Kategoria</span>
            <select value={category} onChange={(e) => setCategory(e.target.value)} className={`${inp} bg-white`}>
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
          <label>
            <span className={lbl}>Tytuł oferty</span>
            <input value={offerTitle} onChange={(e) => setOfferTitle(e.target.value)} placeholder="np. Rozrzutnik obornika — puste: z nazwy modelu" className={inp} />
          </label>
          <label>
            <span className={lbl}>VAT %</span>
            <input type="number" value={vatRate} onChange={(e) => setVatRate(e.target.value)} className={inp} />
          </label>
          <label className="col-span-2">
            <span className={lbl}>Opis (na ofercie)</span>
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className={inp} />
          </label>
        </div>

        <div className="border-t border-slate-100 pt-3 grid grid-cols-2 gap-3">
          <label>
            <span className={lbl}>Cena detaliczna netto ({currency}) — dla klienta</span>
            <input type="number" value={retailPrice} onChange={(e) => setRetailPrice(e.target.value)} className={inp} />
          </label>
          <div />
          <label>
            <span className={lbl}>Cena katalogowa producenta ({currency}) <span className="text-amber-700">tylko właściciel</span></span>
            <input type="number" value={listPrice} onChange={(e) => setListPrice(e.target.value)} className={inp} />
          </label>
          <label>
            <span className={lbl}>Rabat dealerski %</span>
            <input type="number" value={dealerDiscountPct} onChange={(e) => setDealerDiscountPct(e.target.value)} className={inp} disabled={!listPrice} />
          </label>
        </div>
        {listPrice && (
          <p className="text-xs text-slate-500">
            Cena zakupu policzy się sama: {Number(listPrice).toLocaleString('pl-PL')} − {dealerDiscountPct || 0}% ={' '}
            <span className="font-medium text-slate-700">{(Number(listPrice) * (1 - Number(dealerDiscountPct || 0) / 100)).toLocaleString('pl-PL', { maximumFractionDigits: 2 })} {currency}</span>
          </p>
        )}

        {/* Equipment — the rows the salesperson will tick when quoting. */}
        <div className="border-t border-slate-100 pt-3">
          <div className="flex items-baseline justify-between mb-1.5">
            <span className="text-xs uppercase text-slate-500">Wyposażenie dodatkowe</span>
            <span className="text-xs text-slate-400">Grupa = wybór jednego z kilku (np. „Pojemność”); bez grupy = dodatek do zaznaczenia.</span>
          </div>
          <table className="w-full text-sm">
            <thead className="text-xs text-slate-400">
              <tr>
                <th className="text-left font-medium pb-1">Opcja</th>
                <th className="text-left font-medium pb-1 w-28">Cena netto ({currency})</th>
                <th className="text-left font-medium pb-1 w-28">Koszt własny <span className="text-amber-700">właściciel</span></th>
                <th className="text-left font-medium pb-1 w-36">Grupa wyboru</th>
                <th className="text-center font-medium pb-1 w-14" title="Zaznaczona od razu na nowej ofercie; w grupie — wariant bazowy">Dom.</th>
                <th className="w-8" />
              </tr>
            </thead>
            <tbody>
              {options.map((o, i) => (
                <tr key={i}>
                  <td className="pr-2 pb-1"><input value={o.name} onChange={(e) => setOpt(i, { name: e.target.value })} placeholder={i === options.length - 1 ? 'np. Zbiornik na czystą wodę' : ''} className={inp} /></td>
                  <td className="pr-2 pb-1"><input type="number" value={o.price} onChange={(e) => setOpt(i, { price: e.target.value })} className={inp} /></td>
                  <td className="pr-2 pb-1"><input type="number" value={o.purchasePrice} onChange={(e) => setOpt(i, { purchasePrice: e.target.value })} className={inp} /></td>
                  <td className="pr-2 pb-1"><input value={o.optionGroup} onChange={(e) => setOpt(i, { optionGroup: e.target.value })} placeholder="—" className={inp} /></td>
                  <td className="pb-1 text-center"><input type="checkbox" checked={o.defaultSelected} onChange={(e) => setOpt(i, { defaultSelected: e.target.checked })} /></td>
                  <td className="pb-1">
                    {o.name.trim() && (
                      <button type="button" onClick={() => setOptions((l) => l.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-600 text-xs" title="Usuń">✕</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex justify-end gap-2 pt-1">
          <Link to="/katalog" className="text-sm text-slate-500 px-3 py-1.5">Anuluj</Link>
          <button onClick={() => void submit()} disabled={!canSave} className="text-sm bg-brand-600 text-white px-4 py-1.5 rounded-md hover:bg-brand-700 disabled:opacity-50">
            Zapisz{wypelnione.length ? ` z ${wypelnione.length} opcjami` : ''} i przejdź do karty
          </button>
        </div>
      </div>
    </div>
  )
}
